import axios from 'axios';
import Config from './Config';
import { routes } from './Api.config';

// File uploads opt out of the client timeout: a multi-megabyte banner or PDF over a slow
// connection can legitimately outlast it, and aborting one loses the user's upload.
const UPLOAD_CONFIG = { timeout: 0 };

class Api {
  constructor(headers = {}) {
    this.base = axios.create({
      baseURL: Config.API_SERVER,
      headers,
      /*
       * There was no timeout at all, so a request that never came back never rejected.
       * Every fetch action dispatches `showLoading()` first and only clears it in the
       * `then`/`catch`, which means one stalled response left the full-screen loading
       * overlay up permanently with no way out but a page reload.
       *
       * This matters most on the offering investors tab: that endpoint joins each holding
       * onto its user and currently collection-scans `bonds` (no `auction_sno` index), so
       * it is the request most likely to stall under load. 60s is far above its normal
       * response time — it only trips on a genuine hang, and the file uploads below opt
       * out with `timeout: 0` because a slow link legitimately takes longer.
       */
      timeout: 60000,
      params: {
        platform: 'issuerPanel'
      }
    });
    this.attachSessionHandler();
  }

  /*
   * Session handling for a token that expired mid-session.
   *
   * There was no interceptor at all. `PrivateRoute` only checks that a token *exists*
   * in localStorage, never that it is still valid, and the `checkToken` action is
   * defined but never dispatched from anywhere. So once the token expired the user
   * stayed on the dashboard while every request failed, each one surfacing a generic
   * toast built from `handleError` — no redirect, no sign-out.
   *
   * The backend answers an expired/absent token with THREE different statuses:
   *   - 498 from v1.0 routes  (BoomResponse.tokenExpired, src/utils/response.ts:125)
   *   - 403 from v1.1 routes  (BoomResponse.forbidden,   src/utils/response.ts:170)
   *   - 401 for the explicit `unauthorized` helper       (src/utils/response.ts:165)
   * All three mean "re-authenticate", so all three are handled here.
   *
   * `/signin?expired=1` is not a new route: Signin.js already reads `?expired` and
   * raises a "Session Expired" notification. Nothing ever navigated there, so that
   * branch was dead code — this makes it live.
   */
  attachSessionHandler = () => {
    this.base.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 401 || status === 403 || status === 498) {
          // Sign-in itself must keep returning its own error (wrong password is a 400,
          // but an admin-origin rejection is not) rather than bouncing to /signin.
          const url = error?.config?.url || '';
          if (!url.includes(routes.login)) {
            localStorage.removeItem('token');
            localStorage.removeItem('name');
            localStorage.removeItem('avatar');
            localStorage.removeItem('offering_sno');
            localStorage.removeItem('company_sno');
            Api.instance = null;
            if (!window.location.pathname.startsWith('/signin')) {
              window.location.assign('/signin?expired=1');
            }
          }
        }
        return Promise.reject(error);
      }
    );
  };

  static getInstance() {
    if (!Api.instance) {
      Api.instance = new Api();
    }
    return Api.instance;
  }

  static setInstance(token) {
    Api.instance = new Api({
      Authorization: `bearer ${token}`
    });
  }

  addInvestor = async (companySno, investor) => {
    const body = new FormData();
    body.append('name', investor.name);
    body.append('amount', investor.amount);
    body.append('company_sno', companySno);
    body.append('action', 'add-investor');
    try {
      const response = await this.base.post(routes.company.addInvestor, body);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  addTeam = async (companySno, team) => {
    const body = new FormData();
    body.append('team_name', team.team_name);
    body.append('designation', team.designation);
    body.append('team_image', team.team_image, team.team_image.name);
    body.append('company_sno', companySno);
    try {
      const response = await this.base.post(routes.company.addTeam, body, UPLOAD_CONFIG);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  checkToken = async () => {
    try {
      const response = await this.base.get(routes.user.checktoken);
      // `checkToken` answers `{code, status, message}` with no `data` key
      // (UserController.ts:946), so `.data.data` was always undefined.
      return { success: true, data: response.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  deleteComment = async (commentID) => {
    // JSON, not FormData. `validateRemoveComment` reads `offering_sno` from the DELETE
    // *body* (src/utils/validations.ts:719), and express-fileupload — mounted globally at
    // src/app.ts:81 — does not populate req.body for a multipart DELETE, so the field
    // arrived undefined and every delete failed validation with a 400.
    try {
      const response = await this.base.delete(`${routes.offering.comments}/${commentID}`, {
        data: { offering_sno: localStorage.getItem('offering_sno') }
      });
      return { success: true, data: response.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  deleteDocument = async (companySno, id) => {
    // JSON with a NUMERIC id. `documents.id` is written as a number
    // (`new Date().getTime()`, CompanyController.ts:518) and matched with a strict `===`
    // (CompanyController.ts:540). A multipart field arrives as a string, so the match
    // failed, `toDel` stayed undefined and `splice(undefined, 1)` deleted the FIRST
    // document in the array while still answering 200.
    try {
      const response = await this.base.post(routes.company.deleteDocument, {
        id: Number(id),
        company_sno: Number(companySno)
      });
      return { success: true, data: response.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  deleteInvestor = async (companySno, id) => {
    // JSON with a NUMERIC id — identical defect to deleteDocument. `investor.id` is
    // written as `company.investors.length` (a number, CompanyController.ts:263) and
    // compared with `===` at CompanyController.ts:303, so a string id deleted the FIRST
    // investor rather than the requested one.
    try {
      const response = await this.base.post(routes.company.deleteInvestor, {
        id: Number(id),
        company_sno: Number(companySno)
      });
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  deleteReply = async (replyID, commentID) => {
    // JSON for the same reason as deleteComment above.
    try {
      const response = await this.base.delete(
        `${routes.offering.comments}/${commentID}/subComments/${replyID}`,
        { data: { offering_sno: localStorage.getItem('offering_sno') } }
      );
      return { success: true, data: response.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  forgotPassword = async (email) => {
    try {
      const response = await this.base.get(`${routes.forgotPassword}/${email}`);
      return { success: true, data: response.data };
    } catch (e) {
      this.handleError(e);
    }
    return { success: false, data: '' };
  };

  getUser = async () => {
    try {
      const response = await this.base.get(routes.user.getUSer);
      return { success: true, data: response.data };
    } catch (e) {
      this.handleError(e);
    }
    return { success: false, data: '' };
  };

  getComments = async () => {
    try {
      const response = await this.base.get(
        `${routes.offering.comments}?offering_sno=${localStorage.getItem('offering_sno')}`
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getCompany = async (companySno) => {
    try {
      const response = await this.base.get(`${routes.company.overview}?company_sno=${companySno}`);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getDocuments = async (companySno) => {
    try {
      const response = await this.base.get(`${routes.company.document}?company_sno=${companySno}`);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getForms = async () => {
    try {
      const response = await this.base.get(routes.company.forms);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getStates = async () => {
    try {
      const response = await this.base.get(routes.states);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  /* getLocations = async () => {
        try{
            const response = await this.base.get(routes.location.list);
            return {success: true, data: response.data.data};
        }catch(e){
            return this.handleError(e);
        }
    }

    getIndustries = async () => {
        try{
            const response = await this.base.get(routes.industry.list);
            return {success: true, data: response.data.data};
        }catch(e){
            return this.handleError(e);
        }
    } */

  getOfferingOverview = async (OfferingSno) => {
    try {
      const response = await this.base.get(
        `${routes.offering.overview}?offering_sno=${OfferingSno}`
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getPlacedBids = async (OfferingSno, page) => {
    try {
      const response = await this.base.get(
        `${routes.bid.list}?auction_sno=${OfferingSno}&batch=${page}`
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getHistoryPayments = async (OfferingSno) => {
    try {
      const body = new FormData();
      body.append('auction_sno', OfferingSno);
      const response = await this.base.post(routes.portfolio.paymentsHistory, body);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getInvestors = async (OfferingSno) => {
    try {
      const body = new FormData();
      body.append('auction_sno', OfferingSno);
      const response = await this.base.post(routes.portfolio.offeringInvestors, body);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getSchedulePayments = async (OfferingSno) => {
    try {
      const response = await this.base.get(`${routes.amortizationTables.scheduled}/${OfferingSno}`);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getStory = async (OfferingSno) => {
    try {
      const response = await this.base.get(`${routes.offering.about}?offering_sno=${OfferingSno}`);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  getTeams = async (companySno) => {
    try {
      const response = await this.base.get(`${routes.company.team}?company_sno=${companySno}`);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  postComment = async (comment) => {
    const body = new FormData();
    body.append('comment', comment);
    body.append('offering_sno', localStorage.getItem('offering_sno'));
    try {
      const response = await this.base.post(routes.offering.comments, body);
      // `addComment` replies with BoomResponse.success(res, msg, { data: comment })
      // (OfferingController.ts:423), so the payload is `body.data`. `body.comments` has
      // never existed and this read was always undefined.
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  postSubcomment = async (text, commentId) => {
    const body = new FormData();
    body.append('offering_sno', localStorage.getItem('offering_sno'));
    body.append('comment', text);
    try {
      const response = await this.base.post(
        `${routes.offering.comments}/${commentId}/subComments`,
        body
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  signin = async (username, password) => {
    const body = new FormData();
    body.append('username', username);
    body.append('password', password);
    try {
      const response = await this.base.post(routes.login, body);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  // NOTE: currently unreferenced by any view — the request shape below is corrected but
  // untested against a live server for that reason.
  updateAuction = async (auction, auctionSno) => {
    const body = new FormData();
    body.append('auction_sno', auctionSno);
    // `validateAuctionUpdate` (src/utils/validations.ts:343 -> :303) requires BOTH
    // `min_number_for_sale` and `max_number_for_sale` as numerics. The single
    // `number_for_sale` field sent here matched neither, so this call could only ever
    // return a 400 validation error.
    body.append('min_number_for_sale', auction.min_number_for_sale ?? auction.number_for_sale);
    body.append('max_number_for_sale', auction.max_number_for_sale ?? auction.number_for_sale);
    body.append('road_show_start_date_time', auction.road_show_start_date_time);
    body.append('pre_bid_start_date_time', auction.pre_bid_start_date_time);
    body.append('auction_start_date_time', auction.auction_start_date_time);
    body.append('auction_end_date_time', auction.auction_end_date_time);
    try {
      const response = await this.base.put(routes.auction.update, body);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  updateCompany = async (companySno, company) => {
    const body = new FormData();
    body.append('company_sno', companySno);
    // Both spellings are required, and they are not interchangeable. The validator
    // (src/utils/validations.ts:1101) demands `company_name`/`industry_name`, but the
    // writer `updateCompany` (src/services/internal/CompanyService.ts:124-152) reads
    // `data.name`/`data.industry`. Sending only the validated pair passed validation and
    // then silently left the name and industry unchanged.
    body.append('company_name', company.companyName);
    body.append('name', company.companyName);
    body.append('industry_name', company.industry);
    body.append('industry', company.industry);
    body.append('business_type', company.companyType);
    body.append('website', company.website);
    body.append('city', company.city);
    body.append('state', company.state);
    body.append('zip', company.zip);

    if (!company.banner.indexOf) {
      body.append('select_banner_image', company.banner, `banner_${companySno}.jpg`);
    }
    if (!company.ic_banner.indexOf) {
      body.append('select_ic_banner_image', company.ic_banner, `ic_banner_${companySno}.jpg`);
    }
    if (!company.ic_bond_banner.indexOf) {
      body.append(
        'select_ic_bonds_banner_image',
        company.ic_bond_banner,
        `-c_bond_banner_${companySno}.jpg`
      );
    }
    if (!company.bond_certificate_image.indexOf) {
      body.append(
        'select_bond_certificate_image',
        company.bond_certificate_image,
        `bond_certificate_${companySno}.jpg`
      );
    }
    if (!company.social_image.indexOf) {
      body.append('select_social_image', company.social_image, `social_image_${companySno}.jpg`);
    }

    try {
      const response = await this.base.put(routes.company.update, body, UPLOAD_CONFIG);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  updateReply = async (reply) => {
    const body = new FormData();
    body.append('offering_sno', localStorage.getItem('offering_sno'));
    body.append('message', reply.text);
    try {
      const response = await this.base.put(
        `${routes.offering.comments}/${reply.commentId}/subComments/${reply.replyId}`,
        body
      );
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  updateComment = async (commentID, message) => {
    const body = new FormData();
    body.append('offering_sno', localStorage.getItem('offering_sno'));
    body.append(`message`, message);
    try {
      const response = await this.base.put(`${routes.offering.comments}/${commentID}`, body);

      return { success: true, data: response.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  updateSecurity = async (security, auctionSno) => {
    const body = new FormData();
    body.append('symbol', security.symbol);
    body.append('maturity_term', security.maturity_term);
    body.append('auction_sno', auctionSno);
    try {
      const response = await this.base.post(routes.security.update, body);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  updateStory = async (OfferingSno, about) => {
    const body = new FormData();
    body.append('offering_sno', OfferingSno);
    body.append('story', about.story);
    body.append('future_plan', about.future_plan);
    if (about.story_image.name) {
      body.append('story_image', about.story_image, about.story_image.name);
    }
    if (about.future_plan_image.name) {
      body.append('future_plan_image', about.future_plan_image, about.future_plan_image.name);
    }

    try {
      const response = await this.base.put(routes.offering.about, body, UPLOAD_CONFIG);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  uploadDocument = async (document, companySno) => {
    const body = new FormData();
    body.append('company_sno', companySno);
    body.append('documentname', document.name);
    body.append('document_file', document.file, `${document.name}.pdf`);
    try {
      const response = await this.base.post(routes.company.addDocument, body, UPLOAD_CONFIG);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  updateAuctionStatus = async (OfferingSno, action) => {
    const body = new FormData();
    body.append('action', action);
    body.append('auction_sno', OfferingSno);

    try {
      const response = await this.base.post(routes.auction.updateStatus, body);
      // `postUpdateStatus` replies with BoomResponse.success(res, resp) — no `extra`, so
      // no `data` key (AuctionController.ts:365). Note `settle-auction` answers 202, which
      // axios still treats as success.
      return { success: true, data: response.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  updatePassword = async (oldPassword, newPassword) => {
    const body = new FormData();
    body.append('oldpassword', oldPassword);
    body.append('newPassword', newPassword);
    try {
      const response = await this.base.post(routes.changePassword, body);
      // `changePassword` replies `{code, status, message}` with no `data` key
      // (UserController.ts:1675).
      return { success: true, data: response.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  updatePicture = async (userImage) => {
    const body = new FormData();
    body.append('user_image', userImage, userImage.name);
    try {
      const response = await this.base.post(routes.user.userImage, body, UPLOAD_CONFIG);
      return { success: true, data: response.data.data };
    } catch (e) {
      return this.handleError(e);
    }
  };

  // eslint-disable-next-line class-methods-use-this
  handleError = (error) => {
    // A timeout or a dropped connection produces no `response` at all. axios' own wording
    // ("timeout of 60000ms exceeded", "Network Error") is not fit to show an issuer, and
    // the old code fell through to it verbatim.
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
      return { success: false, message: 'The server took too long to respond. Please try again.' };
    }
    if (!error?.response) {
      return {
        success: false,
        message: 'Could not reach the server. Please check your connection and try again.'
      };
    }
    const { message } = error.response.data || { message: error.message };
    // express-validator answers with an ARRAY of `{ msg }` objects; every other route
    // answers with a plain string. This read `message[0].msg` after only testing
    // `typeof message === 'object'` — true for BOTH arrays and plain objects — so a
    // non-array object body threw a TypeError inside the error handler itself and turned
    // a handled API error into an unhandled rejection.
    if (Array.isArray(message)) {
      return { success: false, message: message[0]?.msg || 'Request failed.' };
    }
    if (message && typeof message === 'object') {
      return { success: false, message: message.msg || 'Request failed.' };
    }
    return { success: false, message: message || 'Request failed.' };
  };
}

export default Api;
